<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
    IncludedTicket,
    IncludedDeviation,
    TourEvent,
    EventType,
} from '@/types/delivery-management'

import ShipmentEventContent from '@/components/delivery-management/tour-modal/ShipmentEventContent.vue'
import WaypointEventContent from '@/components/delivery-management/tour-modal/WaypointEventContent.vue'
import TourEventInformation from '@/components/delivery-management/tour-modal/TourEventInformation.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'

export interface Props {
    tourEvents: TourEvent[]
    tickets?: IncludedTicket[]
    deviations?: IncludedDeviation[]
    isShipperCompany?: boolean
    isHubHaulier?: boolean
    showShipmentHaulier?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    isShipperCompany: false,
    isHubHauler: false,
    deviations: () => [],
    tickets: () => [],
})

const { t } = useI18n()

const ticketsByShipment = computed(() => {
    if (props.isHubHaulier) return {}
    const result: Record<string, IncludedTicket[]> = {}
    for (const ticket of props.tickets ?? []) {
        if (!ticket.shipmentId) continue
        result[ticket.shipmentId!] ??= []
        result[ticket.shipmentId].push(ticket)
    }
    return result
})

const deviationsByShipment = computed(() => {
    if (props.isHubHaulier) return {}
    const result: Record<string, IncludedDeviation[]> = {}
    for (const deviation of props.deviations ?? []) {
        if (!deviation.shipmentId) continue
        result[deviation.shipmentId!] ??= []
        result[deviation.shipmentId].push(deviation)
    }
    return result
})
</script>

<template>
    <table class="mx-auto max-w-[1200px]">
        <tr v-for="(event, index) in tourEvents" :key="event.id">
            <td class="relative whitespace-nowrap">
                <TourEventInformation
                    :event="event"
                    :index="index"
                    :deviations="
                        event.shipment ? deviationsByShipment[event.shipment.id] : undefined
                    "
                    :tickets="event.shipment ? ticketsByShipment[event.shipment.id] : undefined"
                />
            </td>
            <td
                :data-id="'eventEntry-' + event.id"
                class="w-full rounded-xl p-0 transition-shadow duration-1000 ease-in-out scroll-mt-5"
            >
                <div v-if="event.type === EventType.ShipmentLoad">
                    <MyPanel bordered>
                        <div class="flex justify-center space-x-2 m-5">
                            <h3
                                class="text-center text-sm font-bold uppercase tracking-wide text-primary-400"
                                v-text="
                                    event.handledAt
                                        ? t('allShipmentsLoaded')
                                        : t('shipmentsNotLoadedYet')
                                "
                            />
                            <mdi:check v-if="event.handledAt" class="text-green-500" />
                        </div>
                    </MyPanel>
                </div>
                <ShipmentEventContent
                    v-if="event.shipment"
                    :event="event"
                    :tickets="ticketsByShipment[event.shipment.id]"
                    :deviations="deviationsByShipment[event.shipment.id]"
                    :is-shipper-company="props.isShipperCompany"
                    :is-hub-haulier="props.isHubHaulier"
                    :show-shipment-haulier="props.showShipmentHaulier"
                />
                <WaypointEventContent v-if="event.waypoint" :event="event" />
            </td>
        </tr>
    </table>
</template>
